<script lang="ts">
	import type { PoiImages } from '../../types';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import * as m from '$paraglide/messages';
	import { createEventDispatcher } from 'svelte';
	import { debounce } from 'throttle-debounce';
	import PoiImage from '../../image/PoiImage.svelte';

	const DEBOUNCE_USER_RESIZE = 100;
	const DEBOUNCE_USER_SCROLL = 10;

	export let borderRadius: string;
	export let images: PoiImages[];
	export let isCarouselOpen = false;
	export let prefixAlt = '';

	const dispatch = createEventDispatcher<{
		imageClick: { carouselOpen: boolean };
		imageScroll: void;
	}>();

	let currentIndex = 0;
	let carouselContainer: HTMLElement;

	$: CAROUSEL_CONTAINER_OPEN_CLASSES = isCarouselOpen
		? 'full-screen bg-coloredneutral inset-0 position-fixed w-screen h-full z-1 p-0'
		: 'w-full relative cursor-pointer';
	$: CAROUSEL_TOGGLE_CLASSES = isCarouselOpen ? 'bg-coloredneutral' : 'bg-neutral-weakest';
	$: IMAGE_TOGGLE_CLASSES = isCarouselOpen
		? 'h-fit md:h-full w-full md:w-auto md:px-5'
		: 'h-full w-full';
	$: WRAPPER_TOGGLE_CLASSES = isCarouselOpen
		? 'h-fit md:flex md:h-full md:w-auto md:flex-row-reverse items-end justify-center'
		: 'h-full overflow-hidden';

	const handleScroll = debounce(DEBOUNCE_USER_SCROLL, () => {
		if (carouselContainer) {
			const scrollLeft = carouselContainer.scrollLeft;
			const imageWidth = carouselContainer.getBoundingClientRect().width;
			const newIndex = Math.round(scrollLeft / imageWidth);
			currentIndex = newIndex;
		}
	});

	const handleResize = debounce(DEBOUNCE_USER_RESIZE, () => {
		scrollToIndexImage(currentIndex);
	});

	function scrollImage(direction: 'right' | 'left' = 'right') {
		if (carouselContainer) {
			const carouselContainerWidth = carouselContainer.getBoundingClientRect().width;
			let scrollDirectionIndex = 0;

			if (direction === 'right') {
				scrollDirectionIndex = currentIndex + 1;
			}
			if (direction === 'left') {
				scrollDirectionIndex = currentIndex - 1;
			}

			carouselContainer.scrollTo({
				left: carouselContainerWidth * scrollDirectionIndex,
				behavior: 'smooth',
			});
		}
	}

	function scrollToIndexImage(index: number) {
		if (carouselContainer) {
			const carouselContainerWidth = carouselContainer.getBoundingClientRect().width;

			// Seems a bit hacky,
			// but Safari does not implement some mechanism for scroll-snap after layout changes like other browsers seems to do,
			// resulting in the carousel not displaying the image at their correct scroll pos when the modal is open.
			// So we need to reset the pos to 0 before calculting the right scroll pos,
			// maybe we should only target Safari for these first scrollTo.
			carouselContainer.scrollTo({
				left: 0,
				behavior: 'instant',
			});

			carouselContainer.scrollTo({
				left: carouselContainerWidth * index,
				behavior: 'instant',
			});
		}
	}

	function toggleModal(status: 'open' | 'close' = 'open') {
		isCarouselOpen = status === 'open';
		setTimeout(() => {
			scrollToIndexImage(currentIndex);
		}, 0);
	}

	$: if (images) {
		scrollToIndexImage(0);
	}
</script>

<svelte:window on:resize={handleResize} />

<svelte:element
	this={isCarouselOpen ? 'dialog' : 'div'}
	class="container-carousel {CAROUSEL_CONTAINER_OPEN_CLASSES}"
	open={isCarouselOpen}
>
	{#if isCarouselOpen}
		<Button
			class="absolute top-2 left-2 z-1"
			color="white"
			hideTextOnMobile
			icon="west"
			onclick={() => toggleModal('close')}
			size="md"
			text={m.poi_datasheet__carousel__close_button()}
			variant="filled"
		/>
	{/if}
	<div class={isCarouselOpen ? 'h-full' : 'h-70'}>
		{#if currentIndex > 0}
			<div class="left-arrow-container absolute left-0 hidden">
				<Button
					color="white"
					icon="west"
					onclick={() => scrollImage('left')}
					size="md"
					variant="ghost"
				/>
			</div>
		{/if}

		<div
			class="carousel flex h-100% overflow-x-scroll {borderRadius} {CAROUSEL_TOGGLE_CLASSES}"
			on:scroll={handleScroll}
			on:scrollend={() => dispatch('imageScroll')}
			bind:this={carouselContainer}
		>
			{#each images as image, index (image)}
				<div
					class="img-container items-center flex grow-0 shrink-0 justify-center w-full"
					on:click={() => {
						dispatch('imageClick', { carouselOpen: isCarouselOpen });
						toggleModal('open');
					}}
					on:keypress={(event) => {
						if (event.key === ' ') {
							toggleModal('open');
						}
					}}
					role="button"
					tabindex="0"
				>
					<div class="img-wrapper w-full {WRAPPER_TOGGLE_CLASSES}">
						<PoiImage
							srcset={image}
							alt={`${prefixAlt} - ${index}`}
							class={IMAGE_TOGGLE_CLASSES}
							objectFit={isCarouselOpen ? 'contain' : 'cover'}
						/>
						{#if image.copyright}
							<span
								class="img-copyright p-2 text-neutral-soft md:translate-x-6 md:translate-y--14 md:translate-z-0 md:transform-scale--100 {isCarouselOpen
									&& index === currentIndex
									? 'visible'
									: 'invisible'}"
							>
								<small>{image.copyright}</small>
							</span>
						{/if}
					</div>
				</div>
			{/each}
		</div>
		{#if images.length > 1}
			<div
				class="indicators items-center bottom-0 flex h-14 justify-center left-50% absolute
					transform-translate-x--50%"
			>
				<!-- eslint-disable-next-line svelte/require-each-key -->
				{#each images as _, index}
					<span
						class:active={index === currentIndex}
						class="rounded-full inline-block h-2 mx-1 w-2 transition-all cursor-default"
					></span>
				{/each}
			</div>
		{/if}

		{#if currentIndex < images.length - 1 && images.length > 1}
			<div class="right-arrow-container absolute right-0 hidden">
				<Button
					color="white"
					icon="east"
					onclick={() => scrollImage('right')}
					size="md"
					variant="ghost"
				/>
			</div>
		{/if}
	</div>
</svelte:element>

<style>
	.carousel {
		-ms-overflow-style: none;
		scrollbar-width: none;
		scroll-snap-type: x mandatory;
	}

	.carousel::-webkit-scrollbar {
		display: none;
	}

	.left-arrow-container,
	.right-arrow-container {
		background-color: rgba(0, 0, 0, 0.1);
		top: 50%;
		transform: translateY(-50%);
	}

	.img-container {
		scroll-snap-align: start;
	}

	.img-copyright {
		@media screen and (min-width: 960px) {
			writing-mode: vertical-rl;
			text-orientation: mixed;
		}
	}

	@media (hover: hover) and (pointer: fine) {
		.left-arrow-container,
		.right-arrow-container {
			display: block;
		}
	}

	.indicators span {
		background-color: rgba(255, 255, 255, 0.6);
	}

	.indicators span.active {
		background-color: white;
	}
</style>
